/**
 * PageBanner — the top of the dashboard page.
 *
 * Composition:
 *   - "Quarterly Review · Q[N] YYYY" eyebrow
 *   - Clinic switcher styled as the page title (native select)
 *   - **NEW V1.1:** Meeting selector dropdown next to the clinic switcher,
 *     styled as the subtitle. Lets the AM pin the dashboard to a past
 *     meeting's snapshot.
 *   - One-line meeting summary with status badge
 *   - **NEW V1.1:** "Back to current" link, top-right, only when pinned
 *
 * The meeting selector navigates by changing the URL:
 *   - "" → /clinics/{cid}                                  (latest)
 *   - "M" → /clinics/{cid}/year/{year}/meeting/{M}         (pinned)
 *
 * `useClinicMeetings` populates the dropdown options. While that query
 * is loading, only the current meeting is shown (matches the clinic-
 * switcher loading-flicker mitigation pattern).
 *
 * Per V1.1 plan §5.2: meetings with `is_pinnable_snapshot_available: false`
 * are rendered as `disabled` <option>s with "(no snapshot)" suffix.
 */
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { StatusBadge } from "@tensaw/visualization";
import type { ClinicDetail, MeetingDetail, MeetingListItem } from "@/api/models";
import { useClinics } from "@/hooks/useClinics";
import { useClinicMeetings } from "@/hooks/useClinicMeetings";
import { formatDateTime, formatDuration } from "@/lib/format";
import type { PinnedContext } from "./types";

/**
 * Earliest performance year selectable in the year dropdown. The dropdown
 * always also includes the year currently being viewed and any year that
 * has a meeting, so years outside this floor still appear.
 */
const MIPS_START_YEAR = 2000;

export interface PageBannerProps {
  clinicId: number;
  year: number;
  clinic: ClinicDetail;
  meeting: MeetingDetail | null;
  pinned: PinnedContext | null;
}

function currentQuarter(): number {
  const month = new Date().getMonth();
  return Math.floor(month / 3) + 1;
}

function reviewStatusKey(status: string | undefined | null): string {
  if (!status) return "scheduled";
  return status.toLowerCase().trim().replace(/-/g, "_");
}

/**
 * Partition meetings into "upcoming" (scheduled, not yet held) and "past"
 * (held or in-progress with held times). Cancelled meetings excluded
 * unless include_cancelled is set on the upstream query (handled by the
 * hook, not here).
 */
function partitionMeetings(items: MeetingListItem[]) {
  const upcoming: MeetingListItem[] = [];
  const past: MeetingListItem[] = [];
  for (const m of items) {
    const status = (m.status ?? "").toLowerCase();
    if (status === "scheduled" || (status === "in_progress" && !m.held_started_at)) {
      upcoming.push(m);
    } else {
      past.push(m);
    }
  }
  return { upcoming, past };
}

function meetingOptionLabel(m: MeetingListItem): string {
  // "Q2 2026 · held May 26"   for past
  // "Q3 2026 · scheduled Jun 24"  for upcoming
  const heldDate = m.held_started_at ?? m.scheduled_at;
  if (!heldDate) return `Q${m.quarter} ${m.performance_year}`;
  // Use a short date (no year — quarter implies year)
  const d = new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(heldDate) ? heldDate : `${heldDate}Z`);
  if (Number.isNaN(d.getTime())) return `Q${m.quarter} ${m.performance_year}`;
  const shortDate = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const verb = m.held_started_at ? "held" : "scheduled";
  const suffix = m.is_pinnable_snapshot_available ? "" : " (no snapshot)";
  return `Q${m.quarter} ${m.performance_year} · ${verb} ${shortDate}${suffix}`;
}

export function PageBanner({ clinicId, year, clinic, meeting, pinned }: PageBannerProps) {
  const clinicsQ = useClinics();
  const meetingsQ = useClinicMeetings(clinicId);
  const navigate = useNavigate();

  const quarter = meeting?.quarter ?? currentQuarter();

  const meetingSummary = useMemo(() => {
    if (!meeting) return null;
    const status = reviewStatusKey(meeting.status);
    const heldStart = meeting.held_started_at;
    const heldEnd = meeting.held_ended_at;
    const duration = formatDuration(heldStart, heldEnd);
    const pieces: string[] = [];
    if (heldStart && heldEnd) {
      pieces.push(`Held ${formatDateTime(heldStart)}`);
      if (duration) pieces.push(duration);
    } else if (meeting.scheduled_at) {
      pieces.push(`Scheduled ${formatDateTime(meeting.scheduled_at)}`);
    }
    pieces.push(`AM ${meeting.am_display_name}`);
    if (meeting.client_attendees && meeting.client_attendees.trim() !== "") {
      pieces.push(`Attendees ${meeting.client_attendees}`);
    }
    return { status, line: pieces.join(" · ") };
  }, [meeting]);

  const clinics = clinicsQ.data?.clinics ?? [];
  const allMeetings = meetingsQ.data?.meetings ?? [];
  const { upcoming, past } = partitionMeetings(allMeetings);

  // Year dropdown options: current year down to MIPS_START_YEAR, plus the
  // year currently being viewed and any year that has a meeting (so a
  // pinned/historic year is always reachable). Deduplicated, newest-first.
  const yearOptions = useMemo(() => {
    const thisYear = new Date().getFullYear();
    const years = new Set<number>();
    for (let y = thisYear; y >= MIPS_START_YEAR; y--) years.add(y);
    years.add(year);
    for (const m of allMeetings) {
      if (typeof m.performance_year === "number") years.add(m.performance_year);
    }
    return [...years].sort((a, b) => b - a);
  }, [year, allMeetings]);

  // Selected value for the meeting dropdown: empty string = "Latest", else
  // the pinned meeting_id.
  const currentMeetingSelectValue = pinned && meeting ? String(meeting.meeting_id) : "";

  const onClinicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextClinicId = Number.parseInt(e.target.value, 10);
    if (!Number.isFinite(nextClinicId) || nextClinicId === clinicId) return;
    navigate(`/clinics/${nextClinicId}`);
  };

  const onYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextYear = Number.parseInt(e.target.value, 10);
    if (!Number.isFinite(nextYear) || nextYear === year) return;
    // Navigate to the year view. Dropping the meeting segment clears any
    // pinned meeting — switching years starts from that year's latest.
    navigate(`/clinics/${clinicId}/year/${nextYear}`);
  };

  const onMeetingChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === "") {
      navigate(`/clinics/${clinicId}`);
      return;
    }
    const mid = Number.parseInt(v, 10);
    if (!Number.isFinite(mid)) return;
    // Find the meeting to get its performance_year for the URL.
    const found = allMeetings.find((m) => m.meeting_id === mid);
    const targetYear = found?.performance_year ?? year;
    navigate(`/clinics/${clinicId}/year/${targetYear}/meeting/${mid}`);
  };

  return (
    <div>
      {/* Top row: eyebrow on left, "Back to current" on right when pinned */}
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-tw-teal-400">
          Quarterly Review · Q{quarter} {year}
        </div>
        {pinned ? (
          <Link
            to={`/clinics/${clinicId}`}
            className="inline-flex items-center gap-1 rounded-button border border-tw-teal-500 bg-white px-3 py-1 text-xs font-semibold text-tw-teal-700 hover:bg-tw-teal-50"
          >
            ← Back to current
          </Link>
        ) : null}
      </div>

      <div className="mt-1 flex items-baseline gap-3 flex-wrap">
        <select
          aria-label="Switch clinic"
          value={String(clinicId)}
          onChange={onClinicChange}
          className="bg-transparent border-0 p-0 m-0 text-2xl font-semibold text-gray-800 focus:outline-none focus:ring-0 cursor-pointer hover:text-tw-teal-700"
          style={{ appearance: "none" }}
        >
          {clinics.length === 0 && (
            <option value={String(clinicId)}>{clinic.name}</option>
          )}
          {clinics.map((c) => (
            <option key={c.clinic_id} value={String(c.clinic_id)}>
              {c.name}
            </option>
          ))}
        </select>
        <span className="text-2xl font-semibold text-gray-400">·</span>

        {/* Year selector — same styling as clinic switcher. Changing the
            year navigates to that year's view and clears any pinned
            meeting (see onYearChange). */}
        <select
          aria-label="Select year"
          value={String(year)}
          onChange={onYearChange}
          className="bg-transparent border-0 p-0 m-0 text-2xl font-semibold text-gray-800 focus:outline-none focus:ring-0 cursor-pointer hover:text-tw-teal-700"
          style={{ appearance: "none" }}
        >
          {yearOptions.map((y) => (
            <option key={y} value={String(y)}>
              {y}
            </option>
          ))}
        </select>
        <span className="text-2xl font-semibold text-gray-400">·</span>

        {/* Meeting selector — same styling as clinic switcher.
            When meetings haven't loaded yet, shows a fallback containing
            just the current meeting (matches the clinic-switcher pattern).
            Disabled <option>s are used for meetings without a snapshot
            (is_pinnable_snapshot_available=false). */}
        <select
          aria-label="Select meeting"
          value={currentMeetingSelectValue}
          onChange={onMeetingChange}
          className="bg-transparent border-0 p-0 m-0 text-2xl font-semibold text-gray-800 focus:outline-none focus:ring-0 cursor-pointer hover:text-tw-teal-700"
          style={{ appearance: "none" }}
        >
          {/* "Latest" pseudo-option always available */}
          <option value="">Current · Q{quarter} {year}</option>
          {upcoming.length > 0 ? (
            <optgroup label="Upcoming">
              {upcoming.map((m) => (
                <option
                  key={m.meeting_id}
                  value={String(m.meeting_id)}
                  disabled={!m.is_pinnable_snapshot_available}
                >
                  {meetingOptionLabel(m)}
                </option>
              ))}
            </optgroup>
          ) : null}
          {past.length > 0 ? (
            <optgroup label="Past">
              {past.map((m) => (
                <option
                  key={m.meeting_id}
                  value={String(m.meeting_id)}
                  disabled={!m.is_pinnable_snapshot_available}
                >
                  {meetingOptionLabel(m)}
                </option>
              ))}
            </optgroup>
          ) : null}
          {/* Defensive fallback: if the meeting we're pinned to isn't in
              the loaded list (race, deleted, etc.), render it as a
              standalone option so the selected value remains valid. */}
          {pinned && meeting && !allMeetings.some((m) => m.meeting_id === meeting.meeting_id) ? (
            <option value={String(meeting.meeting_id)}>
              {meeting.label}
            </option>
          ) : null}
        </select>

        {meetingsQ.isError ? (
          <span className="text-xs text-red-600">· couldn&apos;t load meetings</span>
        ) : null}
      </div>

      {meetingSummary ? (
        <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
          <StatusBadge taxonomy="mips-review-status" status={meetingSummary.status} size="compact" withDot />
          <span>{meetingSummary.line}</span>
        </div>
      ) : (
        <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
          <StatusBadge taxonomy="mips-review-status" status="scheduled" size="compact" withDot />
          <span className="italic">No meeting yet — set a goal or schedule a review to start.</span>
        </div>
      )}
    </div>
  );
}
